import { getMonthPayout } from './projectionTotals';

const EPSILON = 1e-6;

export interface IdentityViolation {
  balance: string;
  open: number;
  close: number;
  expectedClose: number;
  delta: number;
}

// Duck-typed readers for the universal-but-optional strategy-owned slots.
// `strategyState` / `strategyDeltas` are opaque to the engine, but every
// strategy that implements a given mechanic uses the same field names (the
// indemnity-surplus reserve, the joint ShareCare sub-pools, the joint
// per-claimant waiting periods), so the checker can assert their identities
// whenever the fields are present without knowing the concrete strategy.
function readNumeric(slot: unknown, key: string): number | null {
  if (slot == null || typeof slot !== 'object') return null;
  const value = (slot as Record<string, unknown>)[key];
  return typeof value === 'number' ? value : null;
}

// Joint ShareCare 1/3 sub-pools (traditionalLtcStrategy joint model).
interface JointPoolsShape {
  clientIndividual: number;
  partnerIndividual: number;
  shared: number;
}

function readJointPools(strategyState: unknown): JointPoolsShape | null {
  const slot =
    strategyState != null && typeof strategyState === 'object'
      ? (strategyState as Record<string, unknown>).jointPools
      : null;
  const clientIndividual = readNumeric(slot, 'clientIndividual');
  const partnerIndividual = readNumeric(slot, 'partnerIndividual');
  const shared = readNumeric(slot, 'shared');
  if (clientIndividual == null || partnerIndividual == null || shared == null) return null;
  return { clientIndividual, partnerIndividual, shared };
}

// Joint per-claimant elimination-period balances (jointClaimant.ts shape).
function readJointWaitingPeriod(
  strategyState: unknown,
  claimant: 'client' | 'partner',
): WaitingPeriodState | null {
  const slot =
    strategyState != null && typeof strategyState === 'object'
      ? (strategyState as Record<string, unknown>).jointWaitingPeriod
      : null;
  const perClaimant =
    slot != null && typeof slot === 'object'
      ? (slot as Record<string, unknown>)[claimant]
      : null;
  const homeDays = readNumeric(perClaimant, 'homeDays');
  const facilityDays = readNumeric(perClaimant, 'facilityDays');
  if (homeDays == null || facilityDays == null) return null;
  return { homeDays, facilityDays };
}

// Core identity checks. Verifies that each independent pool follows
// `close = open + inflationAccrual − depletion` and that the waiting-period
// counters clamp at zero against the consumed days.
//
// Derived totals (ltcPool = base + eob, cumulative payouts, cumulative
// ineligible cost) are no longer stored — they're computed on demand via
// `projectionTotals`, so there's nothing to drift and nothing to check here.
//
// Universal-but-optional identities (P2-41) fire whenever the strategy carries
// the relevant slots:
//   • indemnity surplus — `closeSurplus = openSurplus + accrued − applied`
//   • joint ShareCare sub-pools — non-negative, and the universal
//     `baseBenefitPool` equals the active claimant's visible composition
//     (their individual sub-pool + the shared sub-pool) at open and close
//   • payout sanity — all payout fields finite, the month's cash payout
//     non-negative, and `ltcPayoutToCost >= ltcCoveragePaid >= 0`
//
// deathBenefit remains strategy-owned (product-specific floors/lumps); each
// strategy's own tests assert it. Cross-row open/close continuity is the
// separate `checkProjectionContinuity` below — it needs the whole stream.
export function checkProjectionIdentities(month: MonthlyEntry): IdentityViolation[] {
  const openState = month.openState;
  const close = month.state;
  const { deltas, facts } = month;
  const violations: IdentityViolation[] = [];

  const check = (balance: string, open: number, expectedClose: number, actualClose: number) => {
    const delta = actualClose - expectedClose;
    if (Math.abs(delta) > EPSILON) {
      violations.push({ balance, open, close: actualClose, expectedClose, delta });
    }
  };

  // Per-pool identities. Each LTC sub-pool follows `close = open +
  // inflationAccrual − depletion`. The EP retroactive lump's base portion
  // is rolled into `deltas.base.depletion` by the strategy, so it does
  // not appear separately here.
  check(
    'baseBenefitPool',
    openState.pools.baseBenefitPool,
    openState.pools.baseBenefitPool + deltas.base.inflationAccrual - deltas.base.depletion,
    close.pools.baseBenefitPool,
  );

  check(
    'extensionOfBenefitsPool',
    openState.pools.extensionOfBenefitsPool,
    openState.pools.extensionOfBenefitsPool + deltas.eob.inflationAccrual - deltas.eob.depletion,
    close.pools.extensionOfBenefitsPool,
  );

  check(
    'homeWaitingPeriodDays',
    openState.waitingPeriod.homeDays,
    Math.max(openState.waitingPeriod.homeDays - facts.waitingPeriodDaysConsumedHome, 0),
    close.waitingPeriod.homeDays,
  );

  check(
    'facilityWaitingPeriodDays',
    openState.waitingPeriod.facilityDays,
    Math.max(openState.waitingPeriod.facilityDays - facts.waitingPeriodDaysConsumedFacility, 0),
    close.waitingPeriod.facilityDays,
  );

  // Indemnity-surplus identity — fires whenever the strategy carries the
  // reserve and its per-row flows (every indemnity-capable strategy does).
  // This is exactly the identity that would have flagged P1-3's frozen
  // surplus on every post-exhaustion row.
  const openSurplus = readNumeric(openState.strategyState, 'indemnitySurplus');
  const closeSurplus = readNumeric(close.strategyState, 'indemnitySurplus');
  const surplusAccrued = readNumeric(deltas.strategyDeltas, 'indemnitySurplusAccrued');
  const surplusApplied = readNumeric(deltas.strategyDeltas, 'indemnitySurplusApplied');
  if (
    openSurplus != null &&
    closeSurplus != null &&
    surplusAccrued != null &&
    surplusApplied != null
  ) {
    check(
      'indemnitySurplus',
      openSurplus,
      openSurplus + surplusAccrued - surplusApplied,
      closeSurplus,
    );
  }

  // Joint ShareCare shared-pool identities. The strategy surfaces the active
  // claimant's drawable capacity (their individual sub-pool + the shared
  // sub-pool) on the universal baseBenefitPool slot — assert that composition
  // at open and close, and that no sub-pool ever goes negative.
  const activeClaimant = month.context.claimant ?? 'client';
  const checkJointComposition = (
    label: string,
    jointPools: JointPoolsShape | null,
    surfacedPool: number,
  ) => {
    if (!jointPools) return;
    const activeIndividual =
      activeClaimant === 'client' ? jointPools.clientIndividual : jointPools.partnerIndividual;
    check(label, surfacedPool, activeIndividual + jointPools.shared, surfacedPool);
    for (const [name, value] of Object.entries(jointPools)) {
      if (value < -EPSILON) {
        violations.push({
          balance: `${label}: jointPools.${name} negative`,
          open: value,
          close: value,
          expectedClose: 0,
          delta: value,
        });
      }
    }
  };
  checkJointComposition(
    'baseBenefitPool(open) vs jointPools',
    readJointPools(openState.strategyState),
    openState.pools.baseBenefitPool,
  );
  checkJointComposition(
    'baseBenefitPool(close) vs jointPools',
    readJointPools(close.strategyState),
    close.pools.baseBenefitPool,
  );

  // Payout sanity — every payout field must be a finite number (NaN/Infinity
  // guard), the month's cash payout must be non-negative, and dollars applied
  // to cost can never be less than the direct cost-coverage portion
  // (`ltcPayoutToCost = ltcCoveragePaid + drained surplus`).
  const { payout } = deltas;
  const roomAndBoardCovered = payout.roomAndBoardCovered ?? 0;
  const payoutFields: [string, number][] = [
    ['ltcCoveragePaid', payout.ltcCoveragePaid],
    ['ltcPayoutToCost', payout.ltcPayoutToCost],
    ['ineligibleCost', payout.ineligibleCost],
    ['indemnityPayout', payout.indemnityPayout],
    ['roomAndBoardCovered', roomAndBoardCovered],
  ];
  let payoutFinite = true;
  for (const [name, value] of payoutFields) {
    if (!Number.isFinite(value)) {
      payoutFinite = false;
      violations.push({
        balance: `payout.${name} not finite`,
        open: value,
        close: value,
        expectedClose: 0,
        delta: Number.NaN,
      });
    }
  }
  if (payoutFinite) {
    const monthPayout = getMonthPayout(month);
    if (!Number.isFinite(monthPayout) || monthPayout < -EPSILON) {
      violations.push({
        balance: 'monthPayout negative or not finite',
        open: monthPayout,
        close: monthPayout,
        expectedClose: 0,
        delta: monthPayout,
      });
    }
    if (payout.ltcCoveragePaid < -EPSILON) {
      violations.push({
        balance: 'payout.ltcCoveragePaid negative',
        open: payout.ltcCoveragePaid,
        close: payout.ltcCoveragePaid,
        expectedClose: 0,
        delta: payout.ltcCoveragePaid,
      });
    }
    if (payout.ltcPayoutToCost < payout.ltcCoveragePaid - EPSILON) {
      violations.push({
        balance: 'payout.ltcPayoutToCost below ltcCoveragePaid',
        open: payout.ltcCoveragePaid,
        close: payout.ltcPayoutToCost,
        expectedClose: payout.ltcCoveragePaid,
        delta: payout.ltcPayoutToCost - payout.ltcCoveragePaid,
      });
    }
    // Indemnity room & board coverage can never be negative or exceed the row's
    // room & board cost (it's funded from this month's surplus, capped at R&B).
    const roomAndBoard = facts.roomAndBoardCost ?? 0;
    if (roomAndBoardCovered < -EPSILON || roomAndBoardCovered > roomAndBoard + EPSILON) {
      violations.push({
        balance: 'payout.roomAndBoardCovered outside [0, roomAndBoardCost]',
        open: roomAndBoardCovered,
        close: roomAndBoardCovered,
        expectedClose: Math.min(Math.max(roomAndBoardCovered, 0), roomAndBoard),
        delta: roomAndBoardCovered,
      });
    }
  }

  // deathBenefit has product-specific identities (CM II depletes by base
  // dollars + EP base lump; floored at the guaranteed minimum). Strategies
  // own that assertion.

  return violations;
}

export function assertProjectionIdentities(month: MonthlyEntry): void {
  const violations = checkProjectionIdentities(month);
  if (violations.length === 0) return;

  const summary = violations
    .map(
      v =>
        `${v.balance}: open=${v.open}, expectedClose=${v.expectedClose}, actualClose=${v.close}, delta=${v.delta}`,
    )
    .join('\n  ');
  throw new Error(
    `Projection identity violation at monthIndex=${month.context.monthIndex} (carePhase=${month.context.carePhase}):\n  ${summary}`,
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Cross-row open/close continuity (P2-41). `processClaimProjection` threads
// `openState = month.state` row to row, so a month's stored openState must be
// the prior month's close state — EXCEPT where joint strategies deliberately
// rewrite the row's openState to the active claimant's view (`rowOpenState`):
//   • `waitingPeriod` — joint rows surface the active claimant's per-spouse
//     balance from `strategyState.jointWaitingPeriod`; continuity holds within
//     each claimant's stream, and within a row the surfaced slot must equal
//     the claimant's entry in the joint map.
//   • `pools.baseBenefitPool` — only the joint ShareCare model rewrites it
//     (active individual + shared, asserted per-month in
//     `checkProjectionIdentities`); every other strategy threads it through
//     the merged stream unchanged.
// Everything else — `strategyState` (which carries the shared pools and both
// spouses' balances), `pools.extensionOfBenefitsPool`, `pools.deathBenefit` —
// passes through the merged stream untouched, so it must match the prior row
// exactly regardless of claimant.
// ───────────────────────────────────────────────────────────────────────────

export interface ContinuityViolation {
  // Index into `months` of the row whose openState breaks continuity.
  monthArrayIndex: number;
  field: string;
  expected: unknown;
  actual: unknown;
}

// Structural compare: numbers within EPSILON, everything else strictly equal;
// objects recurse over the union of keys. strategyState is plain data
// (numbers / booleans / nested records), so this covers every strategy shape.
function compareDeep(
  monthArrayIndex: number,
  field: string,
  expected: unknown,
  actual: unknown,
  out: ContinuityViolation[],
): void {
  if (expected === actual) return;
  if (typeof expected === 'number' && typeof actual === 'number') {
    if (Math.abs(expected - actual) > EPSILON) {
      out.push({ monthArrayIndex, field, expected, actual });
    }
    return;
  }
  if (
    expected != null &&
    actual != null &&
    typeof expected === 'object' &&
    typeof actual === 'object'
  ) {
    const keys = new Set([
      ...Object.keys(expected as Record<string, unknown>),
      ...Object.keys(actual as Record<string, unknown>),
    ]);
    for (const key of keys) {
      compareDeep(
        monthArrayIndex,
        `${field}.${key}`,
        (expected as Record<string, unknown>)[key],
        (actual as Record<string, unknown>)[key],
        out,
      );
    }
    return;
  }
  out.push({ monthArrayIndex, field, expected, actual });
}

export function checkProjectionContinuity(months: MonthlyEntry[]): ContinuityViolation[] {
  const violations: ContinuityViolation[] = [];
  // Last row index per claimant stream ('client' / 'partner' on joint;
  // '__single__' on single-life, where it tracks the global previous row).
  const lastIndexByClaimant = new Map<string, number>();

  months.forEach((month, i) => {
    const open = month.openState;
    const claimant = month.context.claimant ?? 'client';
    const claimantKey = month.context.claimant ?? '__single__';

    if (i > 0) {
      const prevClose = months[i - 1].state;
      // strategyState threads through the merged stream unchanged — it carries
      // the shared pools and BOTH spouses' per-claimant balances on joint runs.
      compareDeep(i, 'openState.strategyState', prevClose.strategyState, open.strategyState, violations);
      compareDeep(
        i,
        'openState.pools.extensionOfBenefitsPool',
        prevClose.pools.extensionOfBenefitsPool,
        open.pools.extensionOfBenefitsPool,
        violations,
      );
      compareDeep(
        i,
        'openState.pools.deathBenefit',
        prevClose.pools.deathBenefit,
        open.pools.deathBenefit,
        violations,
      );
      // baseBenefitPool: rewritten per-row only by the joint ShareCare model
      // (composition vs jointPools is asserted in checkProjectionIdentities;
      // the sub-pools' own continuity is covered by the strategyState compare
      // above). Everywhere else it must thread through unchanged.
      if (readJointPools(open.strategyState) == null) {
        compareDeep(
          i,
          'openState.pools.baseBenefitPool',
          prevClose.pools.baseBenefitPool,
          open.pools.baseBenefitPool,
          violations,
        );
      }
    }

    // Joint rows: the surfaced waitingPeriod must be the active claimant's
    // entry in the joint map (covers each claimant's FIRST row, which has no
    // same-claimant predecessor).
    const jointWp = readJointWaitingPeriod(open.strategyState, claimant);
    if (jointWp) {
      compareDeep(
        i,
        `openState.waitingPeriod (vs strategyState.jointWaitingPeriod.${claimant})`,
        jointWp,
        open.waitingPeriod,
        violations,
      );
    }

    // Waiting-period continuity within the claimant's own stream (the global
    // stream for single-life — joint streams interleave the spouses, who each
    // serve their own elimination period).
    const prevSameClaimant = lastIndexByClaimant.get(claimantKey);
    if (prevSameClaimant != null) {
      compareDeep(
        i,
        'openState.waitingPeriod',
        months[prevSameClaimant].state.waitingPeriod,
        open.waitingPeriod,
        violations,
      );
    }
    lastIndexByClaimant.set(claimantKey, i);
  });

  return violations;
}

export function assertProjectionContinuity(months: MonthlyEntry[]): void {
  const violations = checkProjectionContinuity(months);
  if (violations.length === 0) return;

  const summary = violations
    .map(
      v =>
        `months[${v.monthArrayIndex}] ${v.field}: expected=${JSON.stringify(v.expected)}, actual=${JSON.stringify(v.actual)}`,
    )
    .join('\n  ');
  throw new Error(`Projection continuity violation:\n  ${summary}`);
}
